type GroupName = string | { group_name?: string; name?: string };

type GroupRecord = {
  id?: string | number;
  group_id?: string | number;
  group_type_id?: string | number;
  group_type_name?: string;
  group_name?: string;
};

type GroupSection = {
  id: string | number;
  type: string;
  groups: Array<{
    value: string;
    label: string;
    groupTypeId?: string | number;
    groupTypeName: string;
  }>;
};

type LocationGroups = {
  groups_by_type?: Array<{
    group_type_name?: string;
    names?: GroupName[];
    groups?: GroupName[];
  }>;
  groups?: GroupRecord[];
  group_name?: string | Array<GroupRecord | string>;
};

export const buildGroupTypeSections = ({
  groupTypes = [],
  groups = [],
}: {
  groupTypes?: Array<{ id: string | number; group_type_name?: string }>;
  groups?: GroupRecord[];
} = {}): GroupSection[] => {
  const typeById = new Map(
    groupTypes.map((groupType) => [
      String(groupType.id),
      groupType.group_type_name || "",
    ])
  );
  const sectionMap = new Map<string, GroupSection>();

  groupTypes.forEach((groupType) => {
    const typeName = groupType.group_type_name;
    if (!typeName) return;
    sectionMap.set(typeName, {
      id: groupType.id,
      type: typeName,
      groups: [],
    });
  });

  groups.forEach((group) => {
    const typeName =
      group.group_type_name ||
      typeById.get(String(group.group_type_id)) ||
      "Uncategorized";

    if (!sectionMap.has(typeName)) {
      sectionMap.set(typeName, {
        id: group.group_type_id || typeName,
        type: typeName,
        groups: [],
      });
    }

    sectionMap.get(typeName).groups.push({
      value: String(group.id || group.group_id),
      label: group.group_name,
      groupTypeId: group.group_type_id,
      groupTypeName: typeName,
    });
  });

  return Array.from(sectionMap.values())
    .map((section) => ({
      ...section,
      groups: section.groups
        .filter((group) => group.value !== "undefined" && group.label)
        .sort((left, right) => left.label.localeCompare(right.label)),
    }))
    .filter((section) => section.groups.length);
};

export const flattenGroupSections = (sections: GroupSection[] = []) =>
  sections.flatMap((section) => section.groups || []);

export const normalizeLocationGroupsByType = (
  location: LocationGroups = {}
) => {
  if (Array.isArray(location.groups_by_type)) {
    return location.groups_by_type
      .map((section) => {
        const names = section.names || section.groups || [];

        return {
          type: section.group_type_name || "",
          names: names
            .map((name) =>
              typeof name === "string" ? name : name.group_name || name.name
            )
            .filter(Boolean),
        };
      })
      .filter((section) => section.names.length);
  }

  const groups = Array.isArray(location.groups)
    ? location.groups
    : Array.isArray(location.group_name)
      ? location.group_name
      : [];

  if (groups.length) {
    const sectionMap = new Map();

    groups.forEach((group) => {
      if (typeof group === "string") {
        if (!sectionMap.has("")) {
          sectionMap.set("", { type: "", names: [] });
        }
        sectionMap.get("").names.push(group);
        return;
      }

      const typeName = group.group_type_name || "";
      const groupName = group.group_name;
      if (!groupName) return;

      if (!sectionMap.has(typeName)) {
        sectionMap.set(typeName, { type: typeName, names: [] });
      }

      sectionMap.get(typeName).names.push(groupName);
    });

    return Array.from(sectionMap.values());
  }

  const groupNames = location.group_name;
  if (typeof groupNames === "string" && groupNames.trim()) {
    return [
      {
        type: "Uncategorized",
        names: groupNames
          .split(",")
          .map((name) => name.trim())
          .filter(Boolean),
      },
    ];
  }

  return [];
};
